import React from 'react';
import ElasticsearchSelect from '@client/components/ui/select/UniversalElasticsearchSelect';
import AggregationSelect from '@client/components/ui/select/AggregationSelect';
import RangeSelect from '@client/components/ui/select/RangeSelect';

/**
 * Reusable filter select component
 * @param {Object} config - Filter configuration
 * @param {Object} filters - Current filter values
 * @param {Function} onFilterChange - Filter change handler
 * @param {string} document - Elasticsearch document type
 * @param {Object} aggregations - Aggregation data from API response
 * @param useAggregations
 * @returns {JSX.Element}
 */
const FilterSelect = ({
  config,
  filters,
  onFilterChange,
  document,
  aggregations,
}) => {
  const languageConfig = config.languageConfig || null;

  const aggregationOptions = aggregations?.getOptions
    ? aggregations.getOptions(config.key)
    : [];
  const hasAggregationData =
    aggregations && aggregationOptions && aggregationOptions.length > 0;

  if (!config.type && config?.aggregate) {
    return (
      <AggregationSelect
        filterKey={config.key}
        options={aggregationOptions}
        value={filters[config.key] || []}
        onChange={(values, selectedItems) => {
          onFilterChange(config.key, values, selectedItems);
        }}
        label={config.labelKey}
        showCount={true}
      />
    );
  }

  if (config.type === 'boolean_select') {
    return (
      <ElasticsearchSelect
        document={document}
        label={config.labelKey}
        field={config.field}
        value={filters[config.key] || []}
        onChange={(values, selectedItems) => {
          const transformedItems = selectedItems?.map((item) => ({
            ...item,
            label:
              item.value === true || item.value === 'true'
                ? 'Oui'
                : item.value === false || item.value === 'false'
                  ? 'Non'
                  : item.label,
          }));
          onFilterChange(config.key, values, transformedItems);
        }}
        mode="multiple"
        allowClear
        maxTagCount={1}
        pageSize={20}
        sortBy="count"
        sortOrder="desc"
        showCount={false}
        infinite={true}
        style={{ width: '100%' }}
        labelTransform={(label, value) =>
          ({ true: 'Oui', false: 'Non' })[String(value)] ?? label
        }
      />
    );
  }

  if (config.type === 'range_select') {
    return (
      <RangeSelect
        label={config.labelKey}
        value={filters[config.key] || []}
        onChange={(values, selectedItems) => {
          onFilterChange(config.key, values, selectedItems);
        }}
        mode="multiple"
        allowClear
        maxTagCount={1}
        style={{ width: '100%' }}
      />
    );
  }

  return (
    <ElasticsearchSelect
      document={document}
      label={config.labelKey}
      field={config.field}
      label_field={config.label_field || null}
      value_field={config.value_field || null}
      value={filters[config.key] || []}
      onChange={(values, selectedItems) =>
        onFilterChange(config.key, values, selectedItems)
      }
      mode="multiple"
      allowClear
      maxTagCount={1}
      pageSize={20}
      sortBy="count"
      sortOrder="desc"
      showCount={false}
      infinite={true}
      style={{ width: '100%' }}
      languageFields={languageConfig?.fields}
      languageLabels={languageConfig?.labels}
      defaultLanguage={languageConfig?.defaultLanguage || 'fr'}
      showLanguageSelector={!!languageConfig}
    />
  );
};

/**
 * Reusable filter section component
 * @param {Object} props
 * @param {Array} props.filterConfigs - Array of filter configurations
 * @param {Object} props.filters - Current filter values
 * @param {Function} props.onFilterChange - Filter change handler
 * @param {string} props.document - Elasticsearch document type
 * @param {string} props.className - Optional CSS class
 * @param {Object} props.aggregations - Aggregation data from API response
 * @returns {JSX.Element}
 */
const FilterSection = ({
  filterConfigs,
  filters,
  onFilterChange,
  document,
  className = 'grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
  aggregations,
}) => {
  return (
    <div className={className}>
      {filterConfigs.map((config) => (
        <div key={config.key}>
          <FilterSelect
            config={config}
            filters={filters}
            onFilterChange={onFilterChange}
            document={document}
            aggregations={aggregations}
          />
        </div>
      ))}
    </div>
  );
};

export default FilterSection;
